import React, { Component, Fragment, useState } from 'react'

import {
    PaginationState,
    useReactTable,
    getCoreRowModel,
    ColumnDef,
    flexRender,
    SortingState,
    Row,
    getExpandedRowModel,
    RowData,
    Table as TanstackTable,
    ColumnFiltersState,
    Column,
    ExpandedState,
    RowSelectionState,
    TableOptions
} from '@tanstack/react-table'
import { Pagination, Table, TableBody, TableCell, TableHeaderCell, TableRow, TableHeader, Grid, GridRow, Icon, Form, Dropdown, Input, InputProps, Segment, Checkbox, Message } from 'semantic-ui-react'
import { usePaginationSearchParams } from './parsers/pagination'
import { useSortingSearchParams } from './parsers/sorting'
import { insertComponentsIntoText, isNullOrWhitespace } from '../Utils'
import { useGlobalFilterSearchParams } from './parsers/global-filter'
import { useTranslation2 } from '../../Contexts/TranslationContext2'
import { useColumnFiltersSearchParams } from './parsers/column-filters'
import { BPColumn } from '../BPReactTable'
import { useSessionStorage } from 'usehooks-ts'
import { useQuery, UseQueryResult, QueryErrorResetBoundary } from '@tanstack/react-query'
import { ErrorBoundary } from 'react-error-boundary'
// import { UseQueryResult } from '@tanstack/react-query'

declare module '@tanstack/react-table' {
    //allows us to define custom properties for our columns
    interface ColumnMeta<TData extends RowData, TValue> {
        // filterVariant?: 'text' | 'range' | 'select',
        width?: string | number,
        cellTitle?: boolean,
        Filter?: (column: Column<TData, TValue>) => React.ReactElement,
        hidden?: boolean,
        collapsing?: boolean,
        tableCellProps?: { [key: string]: any }
        tableHeaderProps?: { [key: string]: any }
        defaultFilterValue?: any
    }
}

export const getFilterValue = (filter: ColumnFiltersState, id: string) => {
    return filter.find(f => f.id === id)?.value as string | undefined
}

export function convertColumns<T>(columns: BPColumn<T>[]): ColumnDef<T, any>[] {
    return columns.map(col => {
        const def: ColumnDef<T> = {
            id: col.id,
            header: col.Header as string,
            accessorKey: col.accessor as string,
            cell: (cell) => 'Cell' in col ? <>{(col as any).Cell({ value: cell.getValue<any>(), row: { original: cell.row.original } })}</> : cell.getValue<any>(),
            meta: {
                width: col?.width,
                tableCellProps: col?.tableCellProps,
                tableHeaderProps: col?.tableHeaderProps,
                collapsing: col?.collapsing,
                hidden: col?.hidden,
                Filter: filter => col?.Filter ? (col as any).Filter({
                    setFilter: (id, val) => { filter.setFilterValue(val) },
                    column: { filterValue: filter.getFilterValue() }
                }) : undefined,
                defaultFilterValue: col?.defaultFilterValue,
                // cellTitle: col?.cellTitle,
            }
        };
        return def;
    });
}

type PagedData<T> = {
    rows: T[]
    rowCount: number
}

export type BPReactTable2Options<T> = Pick<TableOptions<T>, 'getRowCanExpand' | 'getRowId'> & {
    id?: string,
    columnDefs: ColumnDef<T, any>[],
    queryKeyPrefix: string
    query: (pagination: PaginationState, sorting: SortingState, globalFilter: string, columnFilters: ColumnFiltersState) => Promise<PagedData<T>>
    refetchInterval?: number,
    staleTime?: number,
    defaultSortingState?: SortingState
    // getRowCanExpand?: (row: Row<T>) => boolean
    renderSubComponent?: (props: { row: Row<T> }) => React.ReactElement
    subHeaderComponent?: React.ReactElement;
    disableSortBy?: boolean;
    stateMode?: 'searchParams' | 'state' | 'sessionStorage';
    enableSelection?: boolean;

}

export function useBpReactTable2<T>({ id = '', stateMode = 'searchParams', getRowId, enableSelection, columnDefs, staleTime, queryKeyPrefix, query, refetchInterval, disableSortBy, getRowCanExpand, defaultSortingState, renderSubComponent, subHeaderComponent }: BPReactTable2Options<T>): BPReactTable2Props<T> {
    const { pagination, setPagination } = useBPPagination({ id, stateMode })
    const { sorting, setSorting } = useBPSorting({ id, stateMode, defaultSortingState })
    const { globalFilter, setGlobalFilter } = useBPGlobalFilter({ id, stateMode })
    const { columnFilters, setColumnFilters } = useBPColumnFilters({ id, stateMode, columnDefs })
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({}) //manage your own row selection state

    const [expanded, setExpanded] = React.useState<ExpandedState>({})

    const dataQuery = useQuery({
        queryKey: [queryKeyPrefix, pagination, sorting, globalFilter, columnFilters],
        queryFn: () => query(pagination, sorting, globalFilter, columnFilters),
        
        placeholderData: (prev) => prev,
        refetchInterval: refetchInterval ?? (120 * 1000), //default to 2 minutes
        staleTime: staleTime,
        throwOnError: false, // Handle errors within the table component
    })

    const defaultData = React.useMemo(() => [], [])

    const table = useReactTable({
        data: dataQuery.data?.rows ?? defaultData,
        columns: columnDefs,
        rowCount: dataQuery.data?.rowCount ?? 0,
        state: {
            sorting,
            pagination,
            globalFilter,
            columnFilters,
            expanded,
            rowSelection
        },
        getCoreRowModel: getCoreRowModel(),

        onRowSelectionChange: setRowSelection, //hoist up the row selection state to your own scope

        enableSorting: !disableSortBy,
        manualSorting: true,
        enableSortingRemoval: false,
        onSortingChange: (updater) => {
            setSorting(old => {
                const newSortingValue = updater instanceof Function ? updater(old) : updater
                setExpanded({}) // collapse all rows when sorting changes
                return newSortingValue
            })
        },

        manualPagination: true,
        onPaginationChange: updater => {
            setPagination(old => {
                const newPaginationValue = updater instanceof Function ? updater(old) : updater
                setExpanded({}) // collapse all rows when filter changes
                return newPaginationValue
            })
        },

        onGlobalFilterChange: (updater) => {
            setGlobalFilter(old => {
                const newFilterValue = updater instanceof Function ? updater(old) : updater
                const changed = (JSON.stringify(old) !== JSON.stringify(newFilterValue));

                if (changed)
                    setPagination({ ...pagination, pageIndex: 0 })

                setExpanded({}) // collapse all rows when filter changes
                return newFilterValue
            })
        },


        manualFiltering: true,
        onColumnFiltersChange: (updater) => {
            setColumnFilters(old => {
                const newColumnFiltersValue = updater instanceof Function ? updater(old) : updater
                const changed = JSON.stringify(old) !== JSON.stringify(newColumnFiltersValue);
                if (changed)
                    setPagination({ ...pagination, pageIndex: 0 })

                setExpanded({}) // collapse all rows when filters change
                return newColumnFiltersValue
            })
        },

        manualExpanding: true,
        onExpandedChange: setExpanded,
        getRowCanExpand: getRowCanExpand,

        getRowId: getRowId,

        debugAll: false,
    })

    return { table, dataQuery, pagination, renderSubComponent, subHeaderComponent, enableSelection };
}

function useBPPagination({ id = '', stateMode = 'searchParams' }: { id?: string, stateMode?: 'searchParams' | 'state' | 'sessionStorage' } = {}): { pagination: PaginationState, setPagination: (updater: ((old: PaginationState) => PaginationState) | PaginationState) => void } {
    const [paginationFromSearchParams, setPaginationFromSearchParams] = usePaginationSearchParams({ pageIndex: `p${id}`, pageSize: `ps${id}` })
    const [paginationFromState, setPaginationFromState] = useState<PaginationState>({ pageIndex: 0, pageSize: 10 })
    const [paginationFromSession, setPaginationFromSession] = useSessionStorage<PaginationState>(`table.${id}.pagination`, { pageIndex: 0, pageSize: 10 })

    const pagination = stateMode == 'state'  ? paginationFromState : stateMode == 'sessionStorage' && !isNullOrWhitespace(id) ? paginationFromSession : paginationFromSearchParams
    const setPagination = stateMode == 'state'  ? setPaginationFromState : stateMode == 'sessionStorage' && !isNullOrWhitespace(id) ? setPaginationFromSession : setPaginationFromSearchParams
    return { pagination, setPagination }
}

function useBPSorting({ id = '', stateMode = 'searchParams', defaultSortingState }: { id?: string, stateMode?: 'searchParams' | 'state' | 'sessionStorage', defaultSortingState?: SortingState } = {}): { sorting: SortingState, setSorting: (updater: ((old: SortingState) => SortingState) | SortingState) => void } {
    const [sortingFromSearchParams, setSortingFromSearchParams] = useSortingSearchParams(`$o${id}`, defaultSortingState ?? [])
    const [sortingFromState, setSortingFromState] = React.useState<SortingState>(defaultSortingState ?? [])
    const [sortingFromSession, setSortingFromSession] = useSessionStorage<SortingState>(`table.${id}.sorting`, defaultSortingState ?? [])

    const sorting = stateMode == 'state' ? sortingFromState : stateMode == 'sessionStorage' && !isNullOrWhitespace(id) ? sortingFromSession : sortingFromSearchParams
    const setSorting = stateMode == 'state' ? setSortingFromState : stateMode == 'sessionStorage' && !isNullOrWhitespace(id) ? setSortingFromSession : setSortingFromSearchParams
    return { sorting, setSorting }
}

function useBPGlobalFilter({ id = '', stateMode = 'searchParams' }: { id?: string, stateMode?: 'searchParams' | 'state' | 'sessionStorage' } = {}): { globalFilter: string, setGlobalFilter: (updater: ((old: string) => string) | string) => void } {
    const [globalFilterFromSearchParams, setGlobalFilterFromSearchParams] = useGlobalFilterSearchParams(`s${id}`)
    const [globalFilterFromSession, setGlobalFilterFromSession] = useSessionStorage<string>(`table.${id}.globalFilter`, '')
    const [globalFilterFromState, setGlobalFilterFromState] = React.useState<string>('')

    const globalFilter = stateMode == 'state' ? globalFilterFromState : stateMode == 'sessionStorage' && !isNullOrWhitespace(id) ? globalFilterFromSession : globalFilterFromSearchParams
    const setGlobalFilter = stateMode == 'state' ? setGlobalFilterFromState : stateMode == 'sessionStorage' && !isNullOrWhitespace(id) ? setGlobalFilterFromSession : setGlobalFilterFromSearchParams
    return { globalFilter, setGlobalFilter }
}

function useBPColumnFilters({ id = '', stateMode = 'searchParams', columnDefs = [] }: { id?: string, columnDefs?: any, stateMode?: 'searchParams' | 'state' | 'sessionStorage' } = {}): { columnFilters: ColumnFiltersState, setColumnFilters: (updater: ((old: ColumnFiltersState) => ColumnFiltersState) | ColumnFiltersState) => void } {
    const columnFilterValues = columnDefs.filter(x => x != null && x.meta?.Filter).map(x => {
        return {
            id: x.id ?? ('accessorKey' in x ? x.accessorKey : '')?.toString() ?? '',
            value: x.meta?.defaultFilterValue
        }
    })

    const [columnFiltersFromSearchParams, setColumnFiltersFromSearchParams] = useColumnFiltersSearchParams(`f${id}`, columnFilterValues)
    const [columnFiltersFromState, setColumnFiltersFromState] = React.useState<ColumnFiltersState>(columnFilterValues)
    const [columnFiltersFromSession, setColumnFiltersFromSession] = useSessionStorage<ColumnFiltersState>(`table.${id}.columnFilters`, () => columnFilterValues)

    const columnFilters = stateMode == 'state' ? columnFiltersFromState : stateMode == 'sessionStorage' && !isNullOrWhitespace(id) ? columnFiltersFromSession : columnFiltersFromSearchParams
    const setColumnFilters = stateMode == 'state' ? setColumnFiltersFromState : stateMode == 'sessionStorage' && !isNullOrWhitespace(id) ? setColumnFiltersFromSession : setColumnFiltersFromSearchParams
    return { columnFilters, setColumnFilters }
}

export type BPReactTable2Props<T> = {
    table: TanstackTable<T>
    dataQuery: UseQueryResult<PagedData<T>, unknown>
    pagination: PaginationState
    renderSubComponent?: (props: { row: Row<T> }) => React.ReactElement
    subHeaderComponent?: React.ReactElement;
    // tableProps: UseBPReactTable2Props<T>,
    forPrint?: boolean
    stickyHeader?: boolean,
    disablePagination?: boolean,
    disableGlobalFilter?: boolean,
    fixed?: boolean,
    singleLine?: boolean,
    celled?: boolean,
    deletedSelector?: (row: T) => boolean;
    rowCssNameProvider?: (row: T) => string;
    enableSelection?: boolean;
}

const BPReactTable2Component = <T,>({ table, dataQuery, pagination, renderSubComponent, subHeaderComponent, forPrint, stickyHeader, disablePagination, disableGlobalFilter, fixed, singleLine, deletedSelector, celled = true, enableSelection = false }: BPReactTable2Props<T>) => {
    const { translations: t } = useTranslation2();
    const isPageable = !disablePagination
    const hasSubComponent = !!renderSubComponent
    const isStickyHeader = stickyHeader
    const hasGlobalFilter = !disableGlobalFilter
    const hasFilter = true;

    // Handle query errors
    if (dataQuery.error) {
        throw dataQuery.error; // Throw to be caught by ErrorBoundary
    }

    return (
        <Form as="div" loading={dataQuery.isFetching}>
            <Grid verticalAlign='middle'>
                <Grid.Row columns={'equal'}>
                    <Grid.Column>
                        {isPageable &&
                            <Form.Field inline>
                                {insertComponentsIntoText(t.dataTable.lengthMenu, {
                                    '_MENU_': <Dropdown
                                        key='pageSizeDD'
                                        compact
                                        selection
                                        options={[10, 25, 50, 100].map(size => { return { key: size, value: size, text: size } })}
                                        value={table.getState().pagination.pageSize}
                                        onChange={(e, dprops) => {
                                            table.setPageSize(Number(dprops.value))
                                        }}
                                    />
                                })}
                            </Form.Field>
                        }
                    </Grid.Column>
                    <Grid.Column textAlign="right" style={{ flex: '0 0 auto', width: 'auto' }}>
                        <Form.Group inline style={{ margin: 0 }}>
                            {subHeaderComponent &&
                                <>
                                    {subHeaderComponent}
                                </>
                            }
                            {hasFilter &&
                                <>
                                    {table.getAllColumns().filter(c => c.columnDef.meta?.Filter).map(c => {
                                        return (

                                            <Form.Field key={`filter-col${c.id}`}>
                                                {c.columnDef.meta?.Filter ? c.columnDef.meta?.Filter(c) : null}
                                            </Form.Field>
                                        )
                                    })}
                                </>
                            }
                            {hasGlobalFilter &&
                                <>
                                    <DebouncedInput
                                        value={table.getState().globalFilter ?? ''}
                                        onChange={value => table.setGlobalFilter(String(value))}
                                        placeholder={t.dataTable.search}
                                        icon={'search'}
                                    />
                                </>
                            }
                        </Form.Group>
                    </Grid.Column>
                </Grid.Row>
            </Grid>
            <Table sortable celled={celled} fixed={fixed} singleLine={singleLine} className={`${forPrint ? 'for-print' : ''} ${isStickyHeader ? 'sticky-header' : ''} ${hasSubComponent ? 'expandable' : ''}`}>
                <TableHeader>
                    {table.getHeaderGroups().map(headerGroup => (
                        <TableRow key={headerGroup.id}>
                            {hasSubComponent &&
                                <TableHeaderCell style={{ width: 45 }} />}

                            {enableSelection &&
                                <TableHeaderCell style={{ width: 45 }}>
                                    <Checkbox
                                        checked={table.getIsAllRowsSelected()}
                                        indeterminate={table.getIsSomeRowsSelected()}
                                        onChange={table.getToggleAllRowsSelectedHandler()} //or getToggleAllPageRowsSelectedHandler
                                    />
                                </TableHeaderCell>}

                            {headerGroup.headers.filter(h => !h.column.columnDef.meta?.hidden).map(header => {
                                return (
                                    <TableHeaderCell key={header.id} colSpan={header.colSpan}
                                        sorted={header.column.getIsSorted() ? (header.column.getIsSorted() === 'asc' ? 'ascending' : 'descending') : undefined}
                                        onClick={header.column.getToggleSortingHandler()}
                                        style={{
                                            width: header.column.columnDef.meta?.width,
                                            cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                        }}
                                        {...(header.column.columnDef.meta?.tableHeaderProps || {})}
                                    >

                                        {header.isPlaceholder ? null : (
                                            flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )
                                        )}
                                    </TableHeaderCell>
                                )
                            })}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows.map(row => {
                        // console.log(row);
                        return (

                            <Fragment key={`${row.id}-fragment`}>
                                <TableRow>
                                    {row.getCanExpand() &&
                                        <Table.Cell textAlign='center'>
                                            <Icon {...{ style: { cursor: 'pointer' } }} onClick={row.getToggleExpandedHandler()} name={row.getIsExpanded() ? 'caret down' : 'caret right'}></Icon>
                                        </Table.Cell>
                                    }

                                    {enableSelection && row.getCanSelect() &&
                                        <Table.Cell textAlign='center'>
                                            <Checkbox
                                                checked={row.getIsSelected()}
                                                disabled={!row.getCanSelect()}
                                                onChange={row.getToggleSelectedHandler()}
                                            />
                                        </Table.Cell>
                                    }
                                    {row.getVisibleCells().filter(cell => !cell.column.columnDef.meta?.hidden).map(cell => {
                                        return (
                                            <TableCell key={cell.id}
                                                title={cell.column.columnDef.meta?.cellTitle ? String(cell.getValue()) : undefined}
                                                collapsing={cell.column.columnDef.meta?.collapsing}
                                                negative={deletedSelector != null && deletedSelector(row.original)}
                                                {...(cell.column.columnDef.meta?.tableCellProps || {})}
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>
                                        )
                                    })}
                                </TableRow>
                                {
                                    hasSubComponent && row.getIsExpanded() ? (
                                        <Table.Row key={`${row.id}-expanded`}>
                                            <Table.Cell colSpan={row.getVisibleCells().filter(cell => !cell.column.columnDef.meta?.hidden).length + (hasSubComponent ? 1 : 0)}>
                                                {renderSubComponent({ row })}
                                            </Table.Cell>
                                        </Table.Row>
                                    ) : null
                                }
                            </Fragment>
                        )
                    })}
                </TableBody>
            </Table>
            <Grid columns={'equal'}>
                <GridRow>
                    <Grid.Column verticalAlign={'middle'}>
                        <div>
                            {insertComponentsIntoText(t.dataTable.info, {
                                '_START_': (pagination.pageIndex) * pagination.pageSize + 1,
                                '_END_': Math.min((pagination.pageIndex) * pagination.pageSize + pagination.pageSize, dataQuery.data?.rowCount > 0 ? dataQuery.data.rowCount : 0),
                                '_TOTAL_': dataQuery.data?.rowCount > 0 ? dataQuery.data.rowCount : 0,
                            })}
                        </div>
                    </Grid.Column>
                    <Grid.Column textAlign='right'>
                        {isPageable && <Pagination
                            lastItem={null}
                            firstItem={null}
                            nextItem={{ content: t.dataTable.paginate.next }}
                            prevItem={{ content: t.dataTable.paginate.previous }}
                            totalPages={table.getPageCount()}
                            activePage={table.getState().pagination.pageIndex + 1}
                            onPageChange={(e, pageProps) => {
                                table.setPageIndex((Number(pageProps.activePage) ?? 1) - 1)
                            }}
                        />}
                    </Grid.Column>
                </GridRow>
            </Grid>
            {/* <Segment>
                <pre>{JSON.stringify(table.getState(), null, 2)}</pre>
            </Segment> */}
        </Form>
    )
}

export const BPReactTable2 = <T,>(props: BPReactTable2Props<T>) => (
    <QueryErrorResetBoundary>
        {({ reset }) => (
            <ErrorBoundary
                onReset={reset}
                fallbackRender={({ resetErrorBoundary }) => (
                    <Segment placeholder style={{ textAlign: "center", padding: '2rem' }}>
                        <Message negative>
                            <Message.Header>An error occurred while rendering the table</Message.Header>
                            <p>Please try again or contact support if the problem persists.</p>
                        </Message>
                        <button onClick={() => resetErrorBoundary()}>Try again</button>
                    </Segment>
                )}>
                <BPReactTable2Component {...props} />
            </ErrorBoundary>
        )}
    </QueryErrorResetBoundary>
);

// function Filter({ column }: { column: Column<any, unknown> }) {
//     const columnFilterValue = column.getFilterValue()
//     const { filterVariant } = column.columnDef.meta ?? {}

//     return filterVariant === 'range' ? (
//         <div>
//             <div className="flex space-x-2">
//                 {/* See faceted column filters example for min max values functionality */}
//                 <DebouncedInput
//                     type="number"
//                     value={(columnFilterValue as [number, number])?.[0] ?? ''}
//                     onChange={value =>
//                         column.setFilterValue((old: [number, number]) => [value, old?.[1]])
//                     }
//                     placeholder={`Min`}
//                     className="w-24 border shadow rounded"
//                 />
//                 <DebouncedInput
//                     type="number"
//                     value={(columnFilterValue as [number, number])?.[1] ?? ''}
//                     onChange={value =>
//                         column.setFilterValue((old: [number, number]) => [old?.[0], value])
//                     }
//                     placeholder={`Max`}
//                     className="w-24 border shadow rounded"
//                 />
//             </div>
//             <div className="h-1" />
//         </div>
//     ) : filterVariant === 'select' ? (
//         <select
//             onChange={e => column.setFilterValue(e.target.value)}
//             value={columnFilterValue?.toString()}
//         >
//             {/* See faceted column filters example for dynamic select options */}
//             <option value="">All</option>
//             <option value="complicated">complicated</option>
//             <option value="relationship">relationship</option>
//             <option value="single">single</option>
//         </select>
//     ) : (
//         <DebouncedInput
//             className="w-36 border shadow rounded"
//             onChange={value => column.setFilterValue(value)}
//             placeholder={`Search...`}
//             type="text"
//             value={(columnFilterValue ?? '') as string}
//         />
//         // See faceted column filters example for datalist search suggestions
//     )
// }

// A typical debounced input react component

function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}: {
    value: string | number
    onChange: (value: string | number) => void
    debounce?: number
} & Omit<InputProps, 'onChange'>) {
    const { translations: t } = useTranslation2();

    const [value, setValue] = React.useState(initialValue)

    React.useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value])

    return (
        <Input {...props}
            value={value} onChange={e => setValue(e.target.value)} />
    )
}



